import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';

export default function LandingPage() {
  const navigate = useNavigate();
  const { setLanguage, t } = useLanguage();
  const { user, loading } = useAuth();
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate('/sell-car', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleLanguageSelect = (language: 'english' | 'arabic' | 'kurdish') => {
    setLanguage(language);
    navigate('/login');
    setShowLanguageModal(false);
  };

  if (loading || user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Brand Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-900 rounded-2xl mb-6 shadow-lg">
            <span className="text-3xl font-bold text-white">S</span>
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mb-2">{t('landing.title')}</h1>
          <p className="text-slate-500 text-base">{t('landing.subtitle')}</p>
        </div>

        {/* Main Content Card */}
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">{t('landing.welcome')}</h2>
            <p className="text-slate-600 leading-relaxed text-sm">
              {t('landing.description')}
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-3 mb-8">
            <div className="flex items-center text-slate-700 text-sm">
              <div className="w-2 h-2 bg-slate-400 rounded-full mr-3"></div>
              <span>{t('landing.feature1')}</span>
            </div>
            <div className="flex items-center text-slate-700 text-sm">
              <div className="w-2 h-2 bg-slate-400 rounded-full mr-3"></div>
              <span>{t('landing.feature2')}</span>
            </div>
            <div className="flex items-center text-slate-700 text-sm">
              <div className="w-2 h-2 bg-slate-400 rounded-full mr-3"></div>
              <span>{t('landing.feature3')}</span>
            </div>
          </div>

          {/* Continue Button */}
          <Button 
            onClick={() => setShowLanguageModal(true)}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-3 px-6 rounded-xl text-sm transition-colors"
            size="lg"
          >
{t('landing.continue')}
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm">{t('landing.copyright')}</p>
          <p className="text-slate-400 text-xs mt-1">{t('landing.developedBy')}</p>
        </div>

        {/* Language Selection Modal */}
        {showLanguageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-900 text-center mb-6">
                {t('sellCar.selectLanguage')}
              </h3>
              <div className="space-y-3">
                <Button
                  onClick={() => handleLanguageSelect('english')}
                  variant="outline"
                  className="w-full justify-start h-12 text-left border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg"
                >
                  <span className="text-base">🇺🇸 English</span>
                </Button>
                <Button
                  onClick={() => handleLanguageSelect('kurdish')}
                  variant="outline"
                  className="w-full justify-start h-12 text-left border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg"
                >
                  <span className="text-base">🇮🇶 کوردی</span>
                </Button>
                <Button
                  onClick={() => handleLanguageSelect('arabic')}
                  variant="outline"
                  className="w-full justify-start h-12 text-left border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg"
                >
                  <span className="text-base">🇸🇦 العربية</span>
                </Button>
              </div>
              <div className="mt-6 text-center">
                <Button
                  onClick={() => setShowLanguageModal(false)}
                  variant="outline"
                  className="border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium"
                >
{t('sellCar.cancel')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
